import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { DDSTypography } from 'den-design-system'
import React from 'react'
import moment from 'moment'
import pallete from '../../global/newPallete'
import { scaleLinear } from 'd3-scale'

const COLORS = {
  high: '#003682', // 100% - 90%
  mediumHigh: '#398AFF', // 75% - 90%
  medium: '#ACCEFF', // 50% - 75%
  low: '#DEDEDE' // Below 50%
}

const getColor = (value: number) => {
  if (value >= 90) return COLORS.high
  if (value >= 75) return COLORS.mediumHigh
  if (value >= 50) return COLORS.medium
  return COLORS.low
}

const CustomTooltip = ({ active, payload, coordinate, viewBox }: any) => {
  if (active && payload && payload[0]?.payload && coordinate && viewBox) {
    const { usedApiCalls, percentage, date, maximum } = payload[0].payload

    // yScale: maps 0–100% to chart pixels
    const yScale = scaleLinear()
      .domain([0, 100])
      .range([viewBox.y + viewBox.height, viewBox.y])

    // Clamp top to 100%, but show real % text
    const clampedPercentage = Math.min(percentage, 100)
    const barTopY = yScale(clampedPercentage)

    // Add offset so tooltip floats above the bar
    const tooltipY = barTopY - 90 // base offset
    const minTop = viewBox.y - 90 // don’t let it go above chart area

    return (
      <div
        className='bg-white shadow-lg rounded-lg p-[12px] border border-gray-200 relative inline-block w-[210px]'
        style={{
          position: 'absolute',
          left: coordinate.x,
          top: Math.max(tooltipY, minTop),
          transform: 'translateX(-50%)',
          zIndex: 1000,
          pointerEvents: 'none'
        }}
      >
        <div
          className='absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0'
          style={{
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid white',
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))'
          }}
        />

        <div className='grid grid-cols-2 gap-y-[4px] text-sm whitespace-nowrap'>
          <span
            style={{
              fontWeight: '600',
              fontSize: '12px',
              color: pallete.colors.textDark3
            }}
          >
            {moment(date).format('DD-MMM-YYYY')}
          </span>
          <span
            style={{
              fontWeight: '600',
              fontSize: '12px',
              color: pallete.colors.textDark3
            }}
            className='text-right'
          >
            {percentage}%
          </span>

          <span
            style={{
              fontWeight: '400',
              fontSize: '12px',
              color: pallete.colors.textDark4
            }}
          >
            Used API calls:
          </span>
          <span
            style={{
              fontWeight: '400',
              fontSize: '12px',
              color: pallete.colors.textDark4
            }}
            className='text-right'
          >
            {usedApiCalls.toLocaleString()}
          </span>

          <span
            style={{
              fontWeight: '400',
              fontSize: '12px',
              color: pallete.colors.textDark3
            }}
          >
            Total API calls:
          </span>
          <span
            style={{
              fontWeight: '400',
              fontSize: '12px',
              color: pallete.colors.textDark4
            }}
            className='text-right'
          >
            {maximum.toLocaleString()}
          </span>
        </div>
      </div>
    )
  }
  return null
}

const renderCustomLegend = (props: any) => {
  const { payload } = props
  return (
    <div className='absolute top-[-48px] right-0 p-[8px]'>
      <ul className='flex flex-row gap-[8px] items-start'>
        {payload.map((entry: any, index: number) => (
          <li key={`item-${index}`} className='flex items-center gap-[8px]'>
            <span
              className='rounded-full'
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: entry.color
              }}
            />
            <span className='text-[14px] text-[#808EA2]'>{entry.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const SFUsageGraph: React.FC<any> = ({ data, days }) => {
  const transformedData = data.map((item: any) => ({
    date: item.usageDate,
    percentage: item.usageInPercent,
    usedApiCalls: item.maximum - item.remaining,
    maximum: item.maximum,
    remaining: item.remaining
  }))

  return (
    <div
      className='p-[16px] bg-white rounded-[12px] border'
      style={{ borderColor: pallete.colors.stroke3 }}
    >
      <div className='flex justify-between items-center mb-[12px]'>
        <DDSTypography.Title
          type='h3'
          variant='semiBold'
          color={pallete.colors.textDark3}
        >
          Summary
        </DDSTypography.Title>
      </div>

      <ResponsiveContainer width='100%' height={460}>
        <BarChart data={transformedData} barSize={40}>
          <CartesianGrid
            strokeDasharray='3 3'
            vertical={false}
            stroke='#E5E7EB'
          />

          <XAxis
            dataKey='date'
            tick={{ fontSize: 13, fill: '#808080' }}
            axisLine={{ stroke: '#D1D5DB' }}
            tickLine={false}
            height={80}
            interval={0}
            angle={days >= 14 ? -90 : 0}
            textAnchor={days >= 14 ? 'end' : 'middle'}
            tickCount={10}
          />

          <YAxis
            domain={[0, 100]}
            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            tickFormatter={(tick) => `${tick}%`}
            tick={{ fontSize: 13, fill: '#808080' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'transparent' }}
            wrapperStyle={{ outline: 'none' }}
            shared={false}
            allowEscapeViewBox={{ x: true, y: true }}
          />

          <Legend
            verticalAlign='top'
            align='right'
            wrapperStyle={{ paddingBottom: '24px' }}
            content={renderCustomLegend}
            payload={[
              { value: '100% - 90%', type: 'circle', color: COLORS.high },
              { value: '90% - 75%', type: 'circle', color: COLORS.mediumHigh },
              { value: '75% - 50%', type: 'circle', color: COLORS.medium },
              { value: 'Below 50%', type: 'circle', color: COLORS.low }
            ]}
          />

          <Bar
            dataKey={(entry: any) => Math.min(entry.percentage, 100)} // clamp height
            radius={[4, 4, 0, 0]}
            barSize={days === 30 ? 40 : 80}
            isAnimationActive={false}
          >
            {transformedData.map((entry: any, index: number) => {
              const value = Math.min(entry.percentage, 100)
              return <Cell key={`cell-${index}`} fill={getColor(value)} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default SFUsageGraph
